import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Avatar,
} from "@mui/material";
import {
  RestaurantMenu as RestaurantMenuIcon,
  AccessTime as AccessTimeIcon,
  LocalFireDepartment as CaloriesIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { getMealTypeColor, getGreyBackground } from "../theme/ClientTheme";

/**
 * Weekly overview showing all 7 days with meal summaries
 * Allows navigation to individual days
 */
const ClientWeeklyOverview = ({ mealPlan, weeklySchedule, onDaySelect }) => {
  const theme = useTheme();
  const daysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const getDayData = (day) => {
    // Use the processed weekly schedule if available, fall back to meal plan structure
    const dayScheduleData =
      weeklySchedule?.[day] || mealPlan?.weeklySchedule?.[day] || {};
    const dayRecipes = [];
    let totalCalories = 0;
    let totalTime = 0;

    if (weeklySchedule && weeklySchedule[day]?.mealTypes) {
      // Use processed weekly schedule structure
      Object.entries(dayScheduleData.mealTypes).forEach(
        ([mealType, scheduledRecipes]) => {
          // For calorie calculation: take the AVERAGE of all recipes for this meal type
          // since the user would choose ONE, not eat all of them
          if (scheduledRecipes.length > 0) {
            const avgCalories =
              scheduledRecipes.reduce((sum, scheduledRecipe) => {
                return (
                  sum +
                  Math.round(
                    (scheduledRecipe.recipe?.kCals || 0) *
                      (scheduledRecipe.servingsPerDay || 1),
                  )
                );
              }, 0) / scheduledRecipes.length;

            const avgTime =
              scheduledRecipes.reduce((sum, scheduledRecipe) => {
                const recipeTime =
                  scheduledRecipe.recipe?.instructions?.reduce(
                    (stepSum, step) => stepSum + (step.duration || 0),
                    0,
                  ) || 0;
                return sum + recipeTime;
              }, 0) / scheduledRecipes.length;

            totalCalories += Math.round(avgCalories);
            totalTime += avgTime;
          }

          // Add all recipes for display purposes
          scheduledRecipes.forEach((scheduledRecipe) => {
            const recipe = scheduledRecipe.recipe;
            if (recipe) {
              dayRecipes.push({ ...recipe, mealType });
            }
          });
        },
      );
    } else {
      // Fall back to original meal plan structure
      Object.entries(dayScheduleData).forEach(([mealType, recipeIds]) => {
        if (Array.isArray(recipeIds) && recipeIds.length > 0) {
          // For the fallback case, we don't have access to the full recipe objects
          // so we'll just add placeholders for display
          recipeIds.forEach((recipeId) => {
            dayRecipes.push({
              id: recipeId,
              name: `Recipe ${recipeId}`,
              mealType,
            });
          });
        }
      });
    }

    return {
      daySchedule: dayScheduleData,
      dayRecipes,
      totalCalories,
      totalTime,
      mealCount: dayRecipes.length,
    };
  };

  const formatDayName = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>
        Weekly Overview
      </Typography>

      <Grid container spacing={3}>
        {daysOfWeek.map((day) => {
          const dayData = getDayData(day);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={day}>
              <Card
                sx={{
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <CardActionArea
                  onClick={() => onDaySelect(day)}
                  sx={{ height: "100%" }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Day name */}
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      {formatDayName(day)}
                    </Typography>

                    {/* Quick stats */}
                    <Stack spacing={2} sx={{ mb: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "primary.light",
                            width: 24,
                            height: 24,
                          }}
                        >
                          <RestaurantMenuIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          {dayData.mealCount} meals
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "success.light",
                            width: 24,
                            height: 24,
                          }}
                        >
                          <CaloriesIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          ~{dayData.totalCalories} kcal
                        </Typography>
                      </Box>

                      {dayData.totalTime > 0 && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "warning.light",
                              width: 24,
                              height: 24,
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 14 }} />
                          </Avatar>
                          <Typography variant="body2" color="text.secondary">
                            ~{Math.round(dayData.totalTime / 60)} min total
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Meal type indicators */}
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {weeklySchedule?.[day]?.mealTypes
                        ? // Use processed structure
                          Object.keys(dayData.daySchedule.mealTypes || {}).map(
                            (mealType) =>
                              dayData.daySchedule.mealTypes[mealType].length >
                                0 && (
                                <Chip
                                  key={mealType}
                                  label={
                                    mealType.charAt(0).toUpperCase() +
                                    mealType.slice(1)
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor: getMealTypeColor(mealType, theme),
                                    color: "white",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                  }}
                                />
                              ),
                          )
                        : // Fall back to original structure
                          Object.keys(dayData.daySchedule).map(
                            (mealType) =>
                              Array.isArray(dayData.daySchedule[mealType]) &&
                              dayData.daySchedule[mealType].length > 0 && (
                                <Chip
                                  key={mealType}
                                  label={
                                    mealType.charAt(0).toUpperCase() +
                                    mealType.slice(1)
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor: getMealTypeColor(mealType, theme),
                                    color: "white",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                  }}
                                />
                              ),
                          )}
                    </Stack>

                    {/* Recipe preview */}
                    {dayData.dayRecipes.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Featured recipes:
                        </Typography>
                        {dayData.dayRecipes.slice(0, 2).map((recipe) => (
                          <Typography
                            key={recipe.id}
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "text.primary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            • {recipe.name}
                          </Typography>
                        ))}
                        {dayData.dayRecipes.length > 2 && (
                          <Typography variant="body2" color="text.secondary">
                            +{dayData.dayRecipes.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Weekly summary */}
      <Card sx={{ mt: 4, bgcolor: getGreyBackground(theme) }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Weekly Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  {weeklySchedule
                    ? new Set(
                        Object.values(weeklySchedule).flatMap((day) =>
                          Object.values(day.mealTypes || {}).flatMap(
                            (scheduledRecipes) =>
                              scheduledRecipes
                                .map((sr) => sr.recipe?.id)
                                .filter(Boolean),
                          ),
                        ),
                      ).size
                    : new Set(
                        Object.values(mealPlan.weeklySchedule || {}).flatMap(
                          (day) => Object.values(day).flat(),
                        ),
                      ).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unique Recipes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "success.main" }}
                >
                  {Math.round(
                    daysOfWeek.reduce(
                      (total, day) => total + getDayData(day).totalCalories,
                      0,
                    ) / 7,
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Daily Calories
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "warning.main" }}
                >
                  {Math.round(
                    daysOfWeek.reduce(
                      (total, day) => total + getDayData(day).totalTime,
                      0,
                    ) / 60,
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Minutes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "info.main" }}
                >
                  {daysOfWeek.reduce(
                    (total, day) => total + getDayData(day).mealCount,
                    0,
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Meals
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientWeeklyOverview;
